import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { pageTypeSelector } from '@/domains/core/page/page.selectors';
import { getCurrency } from '@/domains/core/settings/utils';
import { graphqlProductToPrProductMapper } from '@/domains/core/tracking/utils/graphqlProductToPrProductMapper';
import { Gtm } from '@/domains/core/tracking/utils/Gtm';
import {
  UseFingerprintReconciliationTrackClickItem,
  UseFingerprintReconciliationTrackDisplayItem,
  useFingerprintReconciliationTracking,
} from '@/domains/productDiscovery/Recommendation/hooks/useFingerprintReconciliationTracking';
import { type ProductCardFragment } from '@/domains/productDiscovery/Recommendation/services/graphql/generated/productCard.fragment.generated';

export const usePlaylistTracking = (engineName: string) => {
  const pageType = useSelector(pageTypeSelector) || '';

  const trackDisplayedItem = useCallback<
    UseFingerprintReconciliationTrackDisplayItem<ProductCardFragment>
  >(
    ({ itemsWithIndex, reconciliationData }) => {
      const event = {
        amp_event_name: 'Display playlist',
        event: 'interaction_view item list',
        event_trigger: pageType,
        number_sp_display: 0,
        page_type: pageType,
        playlist_type: 'natural',
        playlist_name: engineName,
        playlist_reconciliation: reconciliationData.playlistReconciliation,
        pr_products: itemsWithIndex.map(({ item, itemIndex }) =>
          graphqlProductToPrProductMapper(item, itemIndex),
        ),
        reco_engine_name: engineName,
      };

      Gtm.push(event);
    },
    [pageType, engineName],
  );

  const trackClickedItem = useCallback<
    UseFingerprintReconciliationTrackClickItem<ProductCardFragment>
  >(
    ({ itemIndex, item, reconciliationData }) => {
      const event = {
        amp_event_name: 'Click on product card',
        currency: getCurrency(),
        event_trigger: pageType,
        event: 'interaction_product click',
        has_bulk_label: false,
        mgr: undefined,
        page_type: pageType,
        playlist_name: engineName,
        playlist_reconciliation: reconciliationData?.playlistReconciliation,
        pr_products: graphqlProductToPrProductMapper(item, itemIndex),
        has_a_highlighted_product: undefined,
        has_promo_product: undefined,
        mf_product_count: undefined,
        pro_product_count: undefined,
      };

      Gtm.push(event);
    },
    [engineName, pageType],
  );

  const data = useFingerprintReconciliationTracking({
    engineTitle: engineName,
    eventTrigger: pageType,
    trackDisplayedItem,
    trackClickedItem,
  });

  return data;
};

usePlaylistTracking.displayName = 'usePlaylistTracking';
